import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Progress } from './ui/progress';
import { Badge } from './ui/badge';
import { Link } from 'react-router-dom';
import {
  Award,
  Users,
  BarChart,
  RotateCcw,
  Play,
  TrendingUp,
  TrendingDown,
  Target
} from 'lucide-react';
import { Header } from './Header';

const RiskRewardGame = () => {
  // Game State
  const [gameState, setGameState] = useState('setup'); // 'setup', 'playing', 'completed', 'report'
  const [sessionId, setSessionId] = useState(null);
  const [playerName, setPlayerName] = useState('');
  const [sessionData, setSessionData] = useState(null);
  const [currentCard, setCurrentCard] = useState(1);
  const [lastResult, setLastResult] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [report, setReport] = useState(null);
  const [extendedAnalysis, setExtendedAnalysis] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  
  // Animation States
  const [selectedDeck, setSelectedDeck] = useState(null);
  const [showCard, setShowCard] = useState(false);
  const [cardFlipped, setCardFlipped] = useState(false);
  const [animatingPoints, setAnimatingPoints] = useState(false);
  const [pointsChange, setPointsChange] = useState(0);

  // Email States
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailAddress, setEmailAddress] = useState('');
  const [isEmailSending, setIsEmailSending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  // Deck Definitions (ohne Risiko-Informationen)
  const decks = {
    deck_a: {
      name: "Stapel A", 
      color: "from-blue-500 to-blue-600",
      icon: <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center text-blue-600 font-bold">A</div>
    },
    deck_b: {
      name: "Stapel B", 
      color: "from-purple-500 to-purple-600",
      icon: <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center text-purple-600 font-bold">B</div>
    },
    deck_c: {
      name: "Stapel C", 
      color: "from-green-500 to-green-600",
      icon: <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center text-green-600 font-bold">C</div>
    }
  };

  // Mapping für Backend API
  const deckMapping = {
    deck_a: 'high_risk',
    deck_b: 'moderate_risk', 
    deck_c: 'low_risk'
  };

  const startGame = async () => {
    if (!playerName.trim()) return;
    
    setIsLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/risk-reward/session/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          player_name: playerName,
          game_type: 'interactive'
        })
      });
      
      const data = await response.json();
      setSessionId(data.session_id);
      setSessionData({...data, total_points: 500}); // Startpunkte: 500
      setGameState('playing');
    } catch (error) {
      console.error('Error starting game:', error);
    }
    setIsLoading(false);
  };

  const drawCard = async (deckKey) => {
    if (!sessionId || currentCard > 50 || isLoading) return;
    
    setSelectedDeck(deckKey);
    setIsLoading(true);
    
    try {
      // Backend API Call ZUERST
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/risk-reward/card/draw`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId,
          deck_key: deckMapping[deckKey],
          card_number: currentCard
        })
      });
      
      const result = await response.json();
      
      // SOFORT die Werte setzen, BEVOR Animation startet
      setLastResult(result);
      setPointsChange(result.points);
      setSessionData(prev => ({
        ...prev,
        total_points: result.total_points,
        cards_played: currentCard
      }));
      setCurrentCard(prev => prev + 1);
      
      // Animation Sequence NACH dem Setzen der Werte
      setTimeout(() => {
        setShowCard(true);
      }, 200);
      
      setTimeout(() => {
        setCardFlipped(true);
        setAnimatingPoints(true);
      }, 800);
      
      // Prüfen ob Spiel beendet
      if (currentCard >= 50) {
        setTimeout(() => {
          setGameState('completed');
        }, 2000);
      }
      
      // Animation zurücksetzen
      setTimeout(() => {
        setSelectedDeck(null);
        setShowCard(false);
        setCardFlipped(false);
        setAnimatingPoints(false);
        setPointsChange(0);
        setIsLoading(false);
      }, 2500);
      
    } catch (error) {
      console.error('Error drawing card:', error);
      setIsLoading(false);
      setSelectedDeck(null);
      setShowCard(false);
      setCardFlipped(false);
    }
  };

  const runSimulation = async () => {
    if (!playerName.trim()) return;
    
    setIsLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/risk-reward/simulation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          player_name: playerName,
          game_type: 'simulation'
        })
      });
      
      const data = await response.json();
      if (data.success) {
        setReport(data.detailed_report);
        setExtendedAnalysis(data.extended_analysis); // Erweiterte Analyse aus Simulation
        setSessionData({
          total_points: data.simulation_results.total_points + 500, // +500 Startpunkte
          risk_profile: data.simulation_results.risk_profile,
          cards_played: 50,
          is_completed: true
        });
        setGameState('report');
      }
    } catch (error) {
      console.error('Error running simulation:', error);
    }
    setIsLoading(false);
  };

  const resetGame = () => {
    setGameState('setup');
    setSessionId(null);
    setSessionData(null);
    setCurrentCard(1);
    setLastResult(null);
    setStatistics(null);
    setReport(null);
    setPlayerName('');
    setSelectedDeck(null);
    setShowCard(false);
    setCardFlipped(false);
    setAnimatingPoints(false);
    setPointsChange(0);
    setShowEmailModal(false);
    setEmailAddress('');
    setIsEmailSending(false);
    setEmailSent(false);
  };

  const getRiskProfileColor = (profile) => {
    if (!profile) return 'bg-gray-100 text-gray-700';
    
    // Neue neutrale Profile
    switch (profile) {
      case 'Sehr sicher':
        return 'bg-blue-50 text-blue-800 border-blue-200';
      case 'Sicher':
        return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'Mittel':
        return 'bg-blue-200 text-blue-900 border-blue-400';
      case 'Erhöhtes Risiko':
        return 'bg-blue-300 text-blue-900 border-blue-500';
      case 'Hohes Risiko':
        return 'bg-blue-400 text-white border-blue-600';
      
      // Fallback für alte Profile
      case 'High Risk Seeker':
        return 'bg-blue-400 text-white border-blue-600';
      case 'Risk Averse':
        return 'bg-blue-50 text-blue-800 border-blue-200';
      case 'Balanced Risk Taker':
        return 'bg-blue-200 text-blue-900 border-blue-400';
      case 'Mixed Risk Profile':
        return 'bg-blue-200 text-blue-900 border-blue-400';
      
      default:
        return 'bg-gray-100 text-gray-700 border-gray-300';
    }
  };

  // Setup Screen
  if (gameState === 'setup') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">

        <Header />

        <div className="max-w-4xl mx-auto p-6 pt-12">
          <Card className="shadow-xl border-0 bg-white/80 backdrop-blur-sm">
            <CardHeader className="text-center">
              <CardTitle className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-4">
                Spieler Selbsttest
              </CardTitle>
              <p className="text-gray-600 text-lg">
                Entdecke dein persönliches Risikoprofil durch strategische Kartenwahl
              </p>
            </CardHeader>
            
            <CardContent className="space-y-8">
              <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-6 rounded-xl">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Spielregeln:</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-gray-700">
                  <div className="space-y-2">
                    <p>• <strong>50 Karten ziehen</strong> aus 3 Stapeln</p>
                    <p>• <strong>500 Punkte</strong> Startkapital</p>
                    <p>• Jede Karte bringt Punkte oder Verluste</p>
                  </div>
                  <div className="space-y-2">
                    <p>• <strong>Keine Vorab-Informationen</strong> über Stapel</p>
                    <p>• <strong>Strategische Entscheidungen</strong> treffen</p>
                    <p>• <strong>Persönliches Risikoprofil</strong> entdecken</p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {Object.entries(decks).map(([key, deck]) => (
                  <Card key={key} className="border-2 hover:shadow-lg transition-all transform hover:scale-105 bg-white/60 backdrop-blur-sm">
                    <CardContent className="p-6 text-center">
                      <div className={`w-full h-32 bg-gradient-to-br ${deck.color} rounded-xl flex items-center justify-center text-white mb-4 shadow-lg`}>
                        {deck.icon}
                      </div>
                      <h4 className="text-xl font-bold text-gray-900">{deck.name}</h4>
                      <p className="text-gray-600 mt-2">Wähle weise...</p>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <div className="space-y-6">
                <div className="bg-white/60 backdrop-blur-sm p-6 rounded-xl">
                  <Label htmlFor="playerName" className="text-lg font-semibold">Dein Name</Label>
                  <Input
                    id="playerName"
                    value={playerName}
                    onChange={(e) => setPlayerName(e.target.value)}
                    placeholder="Gib deinen Namen ein"
                    className="mt-2 text-lg p-3"
                    disabled={isLoading}
                  />
                </div>
                
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button 
                    onClick={startGame} 
                    disabled={!playerName.trim() || isLoading}
                    className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white text-lg py-3 shadow-lg"
                  >
                    {isLoading ? (
                      <div className="flex items-center space-x-2">
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>Spiel wird gestartet...</span>
                      </div>
                    ) : (
                      <div className="flex items-center space-x-2">
                        <Play className="w-5 h-5" />
                        <span>Interaktiv Spielen</span>
                      </div>
                    )}
                  </Button>
                  <Button 
                    onClick={runSimulation} 
                    disabled={!playerName.trim() || isLoading}
                    variant="outline"
                    className="flex-1 text-lg py-3 border-2"
                  >
                    {isLoading ? 'Simulation läuft...' : (
                      <div className="flex items-center space-x-2">
                        <RotateCcw className="w-5 h-5" />
                        <span>Automatische Simulation</span>
                      </div>
                    )}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Playing Screen
  if (gameState === 'playing') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 relative">
        
        {/* Game Progress Header */}
        <div className="bg-white/80 backdrop-blur-md border-b border-gray-100 sticky top-0 z-50">
          <div className="max-w-6xl mx-auto px-6 py-4">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">{sessionData?.player_name}</h2>
                <p className="text-gray-600">Karte {currentCard} von 50</p>
              </div>
              <div className="text-right">
                <p className={`text-4xl font-bold transition-all duration-500 ${animatingPoints ? 'scale-110' : 'scale-100'} ${
                  pointsChange > 0 ? 'text-green-600' : pointsChange < 0 ? 'text-red-600' : 'text-blue-600'
                }`}>
                  {sessionData?.total_points || 500}
                  {animatingPoints && pointsChange !== 0 && (
                    <span className={`text-2xl ml-2 ${pointsChange > 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {pointsChange > 0 ? '+' : ''}{pointsChange}
                    </span>
                  )}
                </p>
                <p className="text-sm text-gray-600">Punkte</p>
              </div>
            </div>
            
            <div className="mt-4">
              <Progress value={(currentCard - 1) / 50 * 100} className="h-3" />
            </div>
          </div>
        </div>

        <div className="max-w-6xl mx-auto p-6">
          
          {/* Deck Selection */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-8">
            {Object.entries(decks).map(([key, deck]) => (
              <Card 
                key={key} 
                className={`cursor-pointer transition-all duration-300 transform hover:scale-105 ${
                  selectedDeck === key ? 'scale-105 shadow-2xl ring-4 ring-blue-400' : 'hover:shadow-xl'
                } ${isLoading ? 'pointer-events-none' : ''}`}
              >
                <CardContent className="p-8">
                  <div 
                    className={`w-full h-40 bg-gradient-to-br ${deck.color} rounded-2xl flex items-center justify-center text-white mb-6 shadow-lg transition-all duration-300 ${
                      selectedDeck === key ? 'animate-pulse' : ''
                    }`}
                    onClick={() => drawCard(key)}
                  >
                    {deck.icon}
                  </div>
                  
                  <h3 className="text-2xl font-bold text-center text-gray-900 mb-4">{deck.name}</h3>
                  
                  <Button 
                    onClick={() => drawCard(key)}
                    disabled={isLoading || currentCard > 50}
                    className="w-full text-lg py-3"
                  >
                    {selectedDeck === key && isLoading ? (
                      <div className="flex items-center space-x-2">
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>Karte wird gezogen...</span>
                      </div>
                    ) : (
                      'Karte ziehen'
                    )}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Card Animation Overlay */}
        {showCard && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="relative">
              {/* Card - OHNE Spiegelung */}
              <div className={`w-64 h-96 bg-white rounded-2xl shadow-2xl border-4 border-gray-200 relative overflow-hidden transition-all duration-500 ${
                cardFlipped ? 'card-flip-enter' : ''
              }`}>
                
                {/* Card Back */}
                <div className={`absolute inset-0 w-full h-full bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center transition-all duration-500 ${
                  cardFlipped ? 'opacity-0 scale-95' : 'opacity-100 scale-100'
                }`}>
                  <div className="text-6xl text-white opacity-30 animate-pulse">?</div>
                </div>
                
                {/* Card Front - Text ist NORMAL lesbar */}
                <div className={`absolute inset-0 w-full h-full flex flex-col items-center justify-center p-6 transition-all duration-500 ${
                  cardFlipped ? 'opacity-100 scale-100' : 'opacity-0 scale-105'
                }`}>
                  <div className={`text-8xl font-bold transition-all duration-300 ${
                    lastResult?.points > 0 ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {lastResult?.points > 0 ? '+' : ''}{lastResult?.points}
                  </div>
                  <p className="text-gray-600 mt-4 text-center font-semibold">
                    {lastResult?.points > 0 ? '🎉 Gewinn!' : '😔 Verlust'}
                  </p>
                  <p className="text-sm text-gray-500 mt-2">
                    aus {decks[selectedDeck]?.name}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Completed Screen - automatisch Report generieren
  if (gameState === 'completed') {
    // Report und erweiterte Analyse automatisch abrufen wenn noch nicht vorhanden
    if (!report && !extendedAnalysis && sessionId && !isLoading) {
      setIsLoading(true);
      
      // Lade beide Reports parallel
      Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/risk-reward/session/${sessionId}/report`)
          .then(response => response.json()),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/risk-reward/session/${sessionId}/extended-analysis`)
          .then(response => response.json())
      ])
      .then(([reportData, extendedData]) => {
        setReport(reportData);
        setExtendedAnalysis(extendedData.extended_analysis);
        setGameState('report');
      })
      .catch(error => {
        console.error('Error generating reports:', error);
        // Fallback: nur Standard-Report laden
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/risk-reward/session/${sessionId}/report`)
          .then(response => response.json())
          .then(reportData => {
            setReport(reportData);
            setGameState('report');
          })
          .catch(fallbackError => {
            console.error('Error generating fallback report:', fallbackError);
          });
      })
      .finally(() => {
        setIsLoading(false);
      });
    }

    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 p-6">
        <div className="max-w-4xl mx-auto">
          <Card className="shadow-2xl bg-white/80 backdrop-blur-sm">
            <CardHeader className="text-center">
              <CardTitle className="text-4xl font-bold bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent mb-4">
                🎉 Spiel beendet! 🎉
              </CardTitle>
              <p className="text-gray-600 text-lg">
                {isLoading ? 'Report wird generiert...' : 'Du hast alle 50 Karten gespielt!'}
              </p>
            </CardHeader>
            
            <CardContent className="space-y-8">
              {isLoading ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                  <p className="text-gray-600">Ihr persönlicher Report wird erstellt...</p>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="text-center p-6 bg-blue-50 rounded-xl">
                      <Award className="w-16 h-16 mx-auto mb-4 text-blue-600" />
                      <h3 className="text-3xl font-bold text-blue-600">{sessionData?.total_points}</h3>
                      <p className="text-gray-600">Endpunktestand</p>
                      <p className="text-sm text-gray-500 mt-2">
                        {sessionData?.total_points > 500 ? '+' : ''}{(sessionData?.total_points || 500) - 500} vom Start
                      </p>
                    </div>
                    <div className="text-center p-6 bg-purple-50 rounded-xl">
                      <Users className="w-16 h-16 mx-auto mb-4 text-purple-600" />
                      <h3 className="text-lg font-semibold">Report wird geladen...</h3>
                      <p className="text-gray-600">Gleich verfügbar</p>
                    </div>
                    <div className="text-center p-6 bg-green-50 rounded-xl">
                      <BarChart className="w-16 h-16 mx-auto mb-4 text-green-600" />
                      <h3 className="text-3xl font-bold text-green-600">50</h3>
                      <p className="text-gray-600">Karten gespielt</p>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const sendReportEmail = async () => {
    if (!emailAddress || !sessionId) return;
    
    setIsEmailSending(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/risk-reward/email/send-report`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId,
          email: emailAddress,
          player_name: sessionData?.player_name || playerName
        })
      });
      
      const result = await response.json();
      if (result.success) {
        setEmailSent(true);
        setTimeout(() => {
          setShowEmailModal(false);
          setEmailSent(false);
          setEmailAddress('');
        }, 3000);
      }
    } catch (error) {
      console.error('Error sending email:', error);
    }
    setIsEmailSending(false);
  };

  // Report Screen
  if (gameState === 'report' && report) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
        <Header />

        <div className="max-w-6xl mx-auto p-6">
          <Card className="shadow-2xl bg-white/80 backdrop-blur-sm mb-8">
            <CardHeader className="text-center">
              <CardTitle className="text-4xl font-bold bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent mb-4">
                🎯 Dein Risikoprofil Report
              </CardTitle>
              <p className="text-gray-600 text-lg">
                Basierend auf deinen Entscheidungen im Spiel
              </p>
            </CardHeader>
            
            <CardContent className="space-y-8">
              {/* Hauptstatistiken */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center p-6 bg-blue-50 rounded-xl">
                  <Award className="w-16 h-16 mx-auto mb-4 text-blue-600" />
                  <h3 className="text-3xl font-bold text-blue-600">{sessionData?.total_points}</h3>
                  <p className="text-gray-600">Endpunktestand</p>
                  <p className="text-sm text-gray-500 mt-2">
                    {sessionData?.total_points > 500 ? '+' : ''}{(sessionData?.total_points || 500) - 500} vom Start
                  </p>
                </div>
                <div className="text-center p-6 bg-purple-50 rounded-xl">
                  <Target className="w-16 h-16 mx-auto mb-4 text-purple-600" />
                  <Badge className={`text-lg px-4 py-2 ${getRiskProfileColor(extendedAnalysis?.profile_name || sessionData?.risk_profile)}`}>
                    {extendedAnalysis?.profile_name || sessionData?.risk_profile || 'Wird analysiert...'}
                  </Badge>
                  <p className="text-gray-600 mt-2">Dein Risikoprofil</p>
                  {extendedAnalysis?.profile_description && (
                    <p className="text-xs text-gray-500 mt-1 italic">
                      {extendedAnalysis.profile_description}
                    </p>
                  )}
                </div>
                <div className="text-center p-6 bg-green-50 rounded-xl">
                  <BarChart className="w-16 h-16 mx-auto mb-4 text-green-600" />
                  <h3 className="text-3xl font-bold text-green-600">
                    {extendedAnalysis?.additional_stats?.longest_win_streak || 0}
                  </h3>
                  <p className="text-gray-600">Längste Gewinnserie</p>
                  <p className="text-sm text-gray-500 mt-2">aus 50 Karten</p>
                </div>
              </div>

              {/* Detaillierte Analyse */}
              {(report || extendedAnalysis) && (
                <div className="space-y-6">
                  {/* Erweiterte Risikoanalyse */}
                  {extendedAnalysis && (
                    <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-6 rounded-xl">
                      <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                        <TrendingUp className="w-6 h-6 mr-2 text-blue-600" />
                        Dein Risikoverhalten (Score: {extendedAnalysis.total_score})
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <h4 className="font-semibold text-gray-800 mb-3">Verhaltensanalyse:</h4>
                          <div className="space-y-3">
                            <div className="flex justify-between items-center">
                              <span className="text-gray-700">Risikobereitschaft:</span>
                              <div className="flex items-center space-x-2">
                                <div className="w-20 bg-gray-200 rounded-full h-2">
                                  <div 
                                    className="bg-blue-600 h-2 rounded-full" 
                                    style={{width: `${Math.min(extendedAnalysis.scores.risk_appetite, 100)}%`}}
                                  ></div>
                                </div>
                                <Badge variant="outline">{extendedAnalysis.scores.risk_appetite.toFixed(1)}</Badge>
                              </div>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-gray-700">Disziplin:</span>
                              <div className="flex items-center space-x-2">
                                <div className="w-20 bg-gray-200 rounded-full h-2">
                                  <div 
                                    className="bg-green-600 h-2 rounded-full" 
                                    style={{width: `${Math.min(extendedAnalysis.scores.discipline, 100)}%`}}
                                  ></div>
                                </div>
                                <Badge variant="outline">{extendedAnalysis.scores.discipline.toFixed(1)}</Badge>
                              </div>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-gray-700">Verlusttoleranz:</span>
                              <div className="flex items-center space-x-2">
                                <div className="w-20 bg-gray-200 rounded-full h-2">
                                  <div 
                                    className="bg-purple-600 h-2 rounded-full" 
                                    style={{width: `${Math.min(extendedAnalysis.scores.drawdown_tolerance, 100)}%`}}
                                  ></div>
                                </div>
                                <Badge variant="outline">{extendedAnalysis.scores.drawdown_tolerance.toFixed(1)}</Badge>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-800 mb-3">Stapel-Verteilung:</h4>
                          <div className="space-y-2">
                            {Object.entries(extendedAnalysis.stack_distribution).map(([stack, data]) => (
                              <div key={stack} className="flex justify-between items-center">
                                <span className="text-gray-700">
                                  {stack === 'High' ? 'Stapel A (High Risk)' : 
                                   stack === 'Moderate' ? 'Stapel B (Moderate Risk)' : 
                                   'Stapel C (Low Risk)'}
                                </span>
                                <div className="flex items-center space-x-2">
                                  <Badge variant="outline">{data.count} × ({data.percentage.toFixed(1)}%)</Badge>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Stapel-Performance */}
                  {extendedAnalysis?.additional_stats?.stack_performance && (
                    <div className="bg-white/60 backdrop-blur-sm p-6 rounded-xl border-l-4 border-green-500">
                      <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                        <BarChart className="w-5 h-5 mr-2 text-green-600" />
                        Bilanz pro Stapel
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {Object.entries(extendedAnalysis.additional_stats.stack_performance).map(([stack, perf]) => (
                          <div key={stack} className="bg-gray-50 p-4 rounded-lg">
                            <h4 className="font-semibold text-gray-800 mb-2">
                              {stack === 'High' ? 'Stapel A' : stack === 'Moderate' ? 'Stapel B' : 'Stapel C'}
                            </h4>
                            <div className="space-y-1 text-sm">
                              <div className="flex justify-between">
                                <span>Gezogen:</span>
                                <span className="font-semibold">{perf.count} ×</span>
                              </div>
                              <div className="flex justify-between">
                                <span>Durchschnitt:</span>
                                <span className={`font-semibold ${perf.average >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                  {perf.average >= 0 ? '+' : ''}{perf.average.toFixed(1)}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span>Gewinnrate:</span>
                                <span className="font-semibold text-blue-600">{perf.win_rate.toFixed(1)}%</span>
                              </div>
                              <div className="flex justify-between">
                                <span>Gesamt:</span>
                                <span className={`font-semibold ${perf.total >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                  {perf.total >= 0 ? '+' : ''}{perf.total}
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Empfehlungen */}
                  {(report?.recommendations || extendedAnalysis) && (
                    <div className="bg-white/60 backdrop-blur-sm p-6 rounded-xl border-l-4 border-blue-500">
                      <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                        <TrendingDown className="w-5 h-5 mr-2 text-blue-600" />
                        Persönliche Empfehlungen
                      </h3>
                      <div className="space-y-3">
                        {/* Standardempfehlungen */}
                        {report?.recommendations && report.recommendations.map((rec, index) => (
                          <div key={index} className="flex items-start space-x-3">
                            <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                            <p className="text-gray-700">{rec}</p>
                          </div>
                        ))}
                        
                        {/* Erweiterte Empfehlungen basierend auf dem Profil */}
                        {extendedAnalysis && (
                          <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                            <h4 className="font-semibold text-gray-800 mb-2">
                              Empfehlung für "{extendedAnalysis.profile_name}"-Profil:
                            </h4>
                            <p className="text-gray-700 text-sm">
                              {extendedAnalysis.profile_description}
                            </p>
                            {extendedAnalysis.scores.loss_chasing > 60 && (
                              <p className="text-amber-700 text-sm mt-2">
                                💡 Tipp: Nach Verlusten gelassen bleiben und nicht zu riskante Entscheidungen treffen.
                              </p>
                            )}
                            {extendedAnalysis.scores.discipline < 40 && (
                              <p className="text-blue-700 text-sm mt-2">
                                💡 Tipp: Eine konsistentere Strategie kann zu stabileren Ergebnissen führen.
                              </p>
                            )}
                            {extendedAnalysis.scores.risk_appetite > 80 && (
                              <p className="text-purple-700 text-sm mt-2">
                                💡 Tipp: Hohe Risikobereitschaft kann zu großen Gewinnen, aber auch Verlusten führen.
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Aktionen */}
              <div className="flex flex-col sm:flex-row gap-4 pt-6 border-t">
                <Button 
                  onClick={() => setShowEmailModal(true)}
                  className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white"
                >
                  📧 Report per E-Mail senden
                </Button>
                <Button 
                  onClick={resetGame}
                  variant="outline"
                  className="flex-1"
                >
                  🔄 Neues Spiel starten
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Email Modal */}
        {showEmailModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-md bg-white">
              <CardHeader>
                <CardTitle className="text-xl font-bold text-center">
                  {emailSent ? '✅ E-Mail gesendet!' : '📧 Report per E-Mail'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {emailSent ? (
                  <div className="text-center py-4">
                    <p className="text-green-600 font-semibold mb-2">
                      Dein Report wurde erfolgreich versendet!
                    </p>
                    <p className="text-gray-600 text-sm">
                      Dieses Fenster schließt sich automatisch...
                    </p>
                  </div>
                ) : (
                  <>
                    <div>
                      <Label htmlFor="email">E-Mail Adresse</Label>
                      <Input
                        id="email"
                        type="email"
                        value={emailAddress}
                        onChange={(e) => setEmailAddress(e.target.value)}
                        placeholder="deine@email.com"
                        className="mt-1"
                        disabled={isEmailSending}
                      />
                    </div>
                    <div className="flex gap-3">
                      <Button 
                        onClick={sendReportEmail}
                        disabled={!emailAddress || isEmailSending}
                        className="flex-1"
                      >
                        {isEmailSending ? (
                          <div className="flex items-center space-x-2">
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            <span>Sende...</span>
                          </div>
                        ) : (
                          'Senden'
                        )}
                      </Button>
                      <Button 
                        onClick={() => setShowEmailModal(false)}
                        variant="outline"
                        disabled={isEmailSending}
                      >
                        Abbrechen
                      </Button>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    );
  }

  return null;
};

export default RiskRewardGame;